import { useClient } from '@cortex/react-query';
import type { CortexClient } from '@cortex/sdk';
import type {
  Agent,
  ModelConfig,
  OrchestrationGroup,
  PersistentAgentGroup,
  RunReflectionNowResult,
} from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import { AgentMemoryAndSkillsSection } from '../../components/agents/AgentMemoryAndSkillsSection';
import { AgentTestModal } from '../../components/agents/AgentTestModal';
import { HandlebarsInstructionsEditor } from '../../components/agents/HandlebarsInstructionsEditor';
import { BackButton } from '../../components/common/BackButton';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { EmptyState } from '../../components/common/EmptyState';
import { JsonSchemaBuilder } from '../../components/common/JsonSchemaBuilder';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { McpToolsEditor } from '../../components/mcp-connections/McpToolsEditor';
import { routes } from '../../lib/constants';
import { formatDateTime } from '../../lib/format';
import { sortModelsOpenAiFirst } from '../../lib/prefer-openai-model';

import {
  isAgentEditorDirty,
  isAgentSaveDisabled,
  readAgentContinuityMode,
  readAgentMemoryDisabled,
  readAgentReflection,
  readSelfDelegationGrant,
} from './-agent-editor-state';
import { isOrchestrationAgent } from './-agent-kind';
import { GroupCard } from './orchestration/index';

export const Route = createFileRoute('/agents/$agentId')({ component: AgentDetailPage });

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };

function AgentDetailPage() {
  const { agentId } = Route.useParams();
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const agent = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => client.http.get(`v1/agents/${agentId}`).json<Agent>(),
  });
  // An orchestration-kind Agent is also a persistent-agent group. Load its
  // extended config so this definition page can show the same thread block as
  // the orchestration fleet page; standard agents make no extra request.
  const orchestrationGroup = useQuery({
    queryKey: ['persistent-agents', agentId, 'detail'],
    queryFn: () =>
      client.http.get(`v1/persistent-agent-groups/${agentId}`).json<PersistentAgentGroup>(),
    enabled: isOrchestrationAgent(agent.data),
  });
  const orchestrationGroups = useQuery({
    queryKey: ['orchestration-groups'],
    queryFn: () => client.http.get('v1/orchestration-groups').json<OrchestrationGroup[]>(),
    enabled: isOrchestrationAgent(agent.data),
  });
  const models = useQuery({
    queryKey: ['model-configs'],
    queryFn: () =>
      client.http.get('v1/model-configs').json<{ items: ModelConfig[]; total: number }>(),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [hitlAllowed, setHitlAllowed] = useState(false);
  const [elicitationMode, setElicitationMode] = useState<'BLOCKING' | 'NON_BLOCKING'>('BLOCKING');
  const [imageDescriptionAllowed, setImageDescriptionAllowed] = useState(false);
  const [videoDescriptionAllowed, setVideoDescriptionAllowed] = useState(false);
  // Code mode (persistent agents): exposes an `execute_typescript` tool so a
  // single turn can orchestrate multiple MCP tools concurrently (Promise.all).
  const [codeMode, setCodeMode] = useState(false);
  // Delegation (persistent agents): lets a thread spawn ephemeral worker
  // threads in its own group (delegate_task / complete_delegation) that report
  // back with a completion event and stop themselves. Peer-grant capability
  // now (self-grant: targetAgentId === agentId, capabilities.delegateTask) —
  // hydrated from and written to the group's `peers`, not agent config.
  const [delegationEnabled, setDelegationEnabled] = useState(false);
  // Host duty / continuity for persistent agents. 'off' (default) = purely
  // reactive; 'mission' = tick only while a mission/goal is open; 'always' =
  // tick within a ~5min window after any real event. See session-host.
  const [continuityMode, setContinuityMode] = useState<'off' | 'mission' | 'always'>('off');
  const [modelConfigId, setModelConfigId] = useState<string>('');
  // Memory ablation (WS-D): 'off' skips memory injection + tools even when a
  // model is set — the clean memory-OFF control (benchmarks / debugging).
  const [memoryDisabled, setMemoryDisabled] = useState(false);
  // Reflection + memory charter (persistent supervisors, WS-A): when enabled the
  // host seeds a reflection heartbeat and appends the charter to the agent's
  // instructions. Charter/prompt/cron are optional per-fleet overrides — left
  // blank, the host falls back to its built-in defaults.
  const [reflectionEnabled, setReflectionEnabled] = useState(false);
  const [reflectionCron, setReflectionCron] = useState('');
  const [reflectionCharter, setReflectionCharter] = useState('');
  const [reflectionPrompt, setReflectionPrompt] = useState('');
  // "Run reflection now" outcome line — the endpoint distinguishes nudged /
  // paused / no-beat / no-thread so the button can explain a no-op instead of
  // silently doing nothing.
  const [reflectionRunFeedback, setReflectionRunFeedback] = useState<string | null>(null);
  const runReflectionNow = useMutation({
    mutationFn: () =>
      client.http
        .post(`v1/persistent-agent-groups/${agentId}/reflection/run`)
        .json<RunReflectionNowResult>(),
    onSuccess: (r) =>
      setReflectionRunFeedback(
        r.nudged > 0
          ? `Nudged ${r.nudged} heartbeat${r.nudged === 1 ? '' : 's'} — fires shortly, when the thread is idle.`
          : r.liveThreads === 0
            ? 'No live threads for this agent — start one first.'
            : r.pausedSkipped > 0
              ? 'The reflection heartbeat is paused — resume it from the thread page.'
              : 'No reflection heartbeat yet — it is seeded when this supervisor starts an operation (save with reflection enabled first).',
      ),
    onError: (err) =>
      setReflectionRunFeedback(err instanceof Error ? err.message : 'Failed to nudge reflection.'),
  });
  // `null` = no structured-output contract; non-null = JSON Schema the runner
  // hands to Mastra as `output` so the LLM produces matching JSON. Edited via
  // <JsonSchemaBuilder>; same Builder/Raw component used by workflow node
  // outputSchema and workflow-level schemas for consistency.
  const [outputSchema, setOutputSchema] = useState<Record<string, unknown> | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [orchestrationGroupChoice, setOrchestrationGroupChoice] = useState('');
  const [newOrchestrationGroupName, setNewOrchestrationGroupName] = useState('');

  useEffect(() => {
    if (agent.data) {
      setName(agent.data.name);
      setDescription(agent.data.description ?? '');
      setInstructions(agent.data.instructions);
      setHitlAllowed(agent.data.hitlAllowed);
      setElicitationMode(agent.data.elicitationMode);
      setImageDescriptionAllowed(agent.data.imageDescriptionAllowed);
      setVideoDescriptionAllowed(agent.data.videoDescriptionAllowed);
      setModelConfigId(agent.data.modelConfigId ?? '');
      setOutputSchema(agent.data.outputSchema ?? null);
      setCodeMode(Boolean(agent.data.config.codeMode));
      setContinuityMode(readAgentContinuityMode(agent.data.config));
      setMemoryDisabled(readAgentMemoryDisabled(agent.data.config));
      const reflection = readAgentReflection(agent.data.config);
      setReflectionEnabled(reflection.reflectionEnabled);
      setReflectionCron(reflection.reflectionCron);
      setReflectionCharter(reflection.reflectionCharter);
      setReflectionPrompt(reflection.reflectionPrompt);
    }
  }, [agent.data]);

  useEffect(() => {
    if (orchestrationGroup.data?.orchestrationGroupId) {
      setOrchestrationGroupChoice(orchestrationGroup.data.orchestrationGroupId);
    }
  }, [orchestrationGroup.data?.orchestrationGroupId]);

  // "Can create dashboards/widgets" lives on the orchestration config (not the
  // Agent row), so it applies immediately via its own groups PATCH — same
  // pattern as moving orchestration groups. Takes effect on the next build.
  const [dashboardsAllowed, setDashboardsAllowed] = useState(false);
  useEffect(() => {
    setDashboardsAllowed(orchestrationGroup.data?.dashboardsAllowed ?? false);
  }, [orchestrationGroup.data?.dashboardsAllowed]);
  useEffect(() => {
    setDelegationEnabled(readSelfDelegationGrant(agentId, orchestrationGroup.data?.peers));
  }, [agentId, orchestrationGroup.data?.peers]);
  const toggleDashboardsAllowed = useMutation({
    mutationFn: (next: boolean) =>
      client.http
        .patch(`v1/persistent-agent-groups/${agentId}`, { json: { dashboardsAllowed: next } })
        .json<PersistentAgentGroup>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['persistent-agents', agentId, 'detail'] }),
    onError: () => setDashboardsAllowed(orchestrationGroup.data?.dashboardsAllowed ?? false),
  });

  const moveOrchestrationGroup = useMutation({
    mutationFn: () =>
      client.http
        .patch(`v1/persistent-agent-groups/${agentId}`, {
          json:
            orchestrationGroupChoice === '__new__'
              ? { newOrchestrationGroup: { name: newOrchestrationGroupName.trim() } }
              : { orchestrationGroupId: orchestrationGroupChoice },
        })
        .json<PersistentAgentGroup>(),
    onSuccess: async () => {
      setNewOrchestrationGroupName('');
      await qc.invalidateQueries({ queryKey: ['persistent-agents', agentId, 'detail'] });
      fork(qc.invalidateQueries({ queryKey: ['orchestration-groups'] }));
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents'] }));
      fork(qc.invalidateQueries({ queryKey: ['agents'] }));
    },
  });

  const update = useMutation({
    mutationFn: async () => {
      // Prisma replaces the whole `config` JSON on update (no merge), so spread
      const config: Record<string, unknown> = { ...(agent.data?.config ?? {}) };
      if (codeMode) config.codeMode = true;
      else delete config.codeMode;
      // Continuity: 'off' removes the key (default reactive); otherwise store
      // the explicit shape the host resolves (mode + enabled). Always clear the
      // legacy `hostDuty` alias — the host + reader still honor it, so leaving
      // it behind would keep continuity on even after the operator picks 'off'.
      delete config.hostDuty;
      if (continuityMode === 'off') delete config.continuity;
      else config.continuity = { enabled: true, mode: continuityMode };
      const memory: Record<string, unknown> = { ...(agent.data?.config?.memory ?? {}) };
      // The observer model is not per-agent anymore (deployment default only)
      // — actively clean any stale stored choice so old rows converge.
      delete memory.modelConfigId;
      // WS-D: 'off' ablates the memory block + tools (NOT Observational
      // Memory); any other state clears the key for the normal default.
      if (memoryDisabled) memory.mode = 'off';
      else delete memory.mode;
      if (Object.keys(memory).length > 0) config.memory = memory;
      else delete config.memory;
      // Reflection (WS-A): 'off' removes the key (default reactive). When on,
      // store enabled + only the overrides the operator actually filled in —
      // an empty charter/prompt/cron leaves the host on its built-in defaults.
      if (reflectionEnabled) {
        // Start from the STORED reflection object so keys this editor doesn't
        // surface (judgePrompt / judgeModelConfigId, set via the API's
        // behaviorConfig door) survive a save instead of being dropped.
        const stored = config.reflection;
        const reflection: Record<string, unknown> =
          stored && typeof stored === 'object' && !Array.isArray(stored)
            ? { ...(stored as Record<string, unknown>) }
            : {};
        reflection.enabled = true;
        if (reflectionCron.trim()) reflection.cron = reflectionCron.trim();
        else delete reflection.cron;
        if (reflectionCharter.trim()) reflection.charter = reflectionCharter;
        else delete reflection.charter;
        if (reflectionPrompt.trim()) reflection.prompt = reflectionPrompt;
        else delete reflection.prompt;
        config.reflection = reflection;
      } else {
        delete config.reflection;
      }
      // Delegation is a self-grant peer capability now, not agent config —
      // only touch the group's peers when the checkbox actually changed, via
      // the same read-modify-write recipe as the Permissions editor: keep
      // every other grant verbatim, and this row's other capabilities too.
      const currentPeers = orchestrationGroup.data?.peers ?? [];
      if (delegationEnabled !== readSelfDelegationGrant(agentId, currentPeers)) {
        const selfGrant = currentPeers.find((peer) => peer.targetAgentId === agentId);
        const selfCapabilities: Record<string, unknown> = { ...(selfGrant?.capabilities ?? {}) };
        if (delegationEnabled) selfCapabilities.delegateTask = {};
        else delete selfCapabilities.delegateTask;
        const selfHasCapabilities =
          selfCapabilities.listThreads === true ||
          selfCapabilities.startThread === true ||
          'stopThread' in selfCapabilities ||
          'sendEvent' in selfCapabilities ||
          'broadcastEvent' in selfCapabilities ||
          'putFact' in selfCapabilities ||
          'delegateTask' in selfCapabilities ||
          'manageWorkers' in selfCapabilities;
        const nextPeers: Array<{ targetAgentId: string; capabilities: unknown }> =
          currentPeers.filter((peer) => peer.targetAgentId !== agentId);
        if (selfHasCapabilities) {
          nextPeers.push({ targetAgentId: agentId, capabilities: selfCapabilities });
        }
        await client.http
          .patch(`v1/persistent-agent-groups/${agentId}`, { json: { peers: nextPeers } })
          .json<PersistentAgentGroup>();
      }
      return client.http
        .patch(`v1/agents/${agentId}`, {
          json: {
            name,
            description: description.trim() || undefined,
            instructions,
            hitlAllowed,
            elicitationMode,
            imageDescriptionAllowed,
            videoDescriptionAllowed,
            // Empty string in the select = clear back to inherit-default.
            modelConfigId: modelConfigId || null,
            // `null` clears the contract; a non-empty schema sets it. Unlike
            // WorkflowNode.outputSchema (which uses `{}` as the "no schema"
            // sentinel), Agent.outputSchema is nullable in Prisma so we send
            // `null` directly.
            outputSchema,
            config,
          },
        })
        .json<Agent>();
    },
    onSuccess: () => {
      fork(qc.invalidateQueries({ queryKey: ['agent', agentId] }));
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents', agentId, 'detail'] }));
    },
  });

  const remove = useMutation({
    // ORCHESTRATION-kind agents go through the persistent-agent-groups
    // endpoint, which also stops any of the agent's live threads.
    mutationFn: () => client.http.delete(`v1/persistent-agent-groups/${agentId}`),
    onSuccess: async () => {
      try {
        await qc.invalidateQueries({ queryKey: ['agents'] });
        if (isOrchestrationAgent(agent.data)) {
          await qc.invalidateQueries({ queryKey: ['orchestration-groups'] });
          await qc.invalidateQueries({ queryKey: ['persistent-agents'] });
        }
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.AGENTS });
      } catch (err) {
        console.error(err);
      }
    },
  });

  // Dirty check: compare each edited field against the saved agent, using the
  // SAME normalization the seeding effect applies (so reverting a field back
  // to its original value clears the dirty flag). The Save button stays
  // disabled until something actually differs. `outputSchema` is compared by
  // value via JSON since it's an object/null. Delegation lives outside
  // `isAgentEditorDirty` — it's a group-peers self-grant, not an Agent field —
  // so it's OR'd in here against the live peers, not the (Agent-only) draft.
  const isDirty = useMemo(
    () =>
      isAgentEditorDirty(agent.data, {
        name,
        description,
        instructions,
        hitlAllowed,
        elicitationMode,
        imageDescriptionAllowed,
        videoDescriptionAllowed,
        codeMode,
        continuityMode,
        modelConfigId,
        outputSchema,
        memoryDisabled,
        reflectionEnabled,
        reflectionCron,
        reflectionCharter,
        reflectionPrompt,
      }) || delegationEnabled !== readSelfDelegationGrant(agentId, orchestrationGroup.data?.peers),
    [
      agent.data,
      name,
      description,
      instructions,
      hitlAllowed,
      elicitationMode,
      imageDescriptionAllowed,
      videoDescriptionAllowed,
      modelConfigId,
      outputSchema,
      codeMode,
      continuityMode,
      memoryDisabled,
      reflectionEnabled,
      reflectionCron,
      reflectionCharter,
      reflectionPrompt,
      agentId,
      delegationEnabled,
      orchestrationGroup.data?.peers,
    ],
  );

  if (agent.isLoading) return <Spinner />;
  if (agent.error)
    return (
      <div className="alert alert--danger">Failed to load: {(agent.error as Error).message}</div>
    );
  if (!agent.data) return <EmptyState title="Not found" />;

  return (
    <>
      <PageHeader
        title={agent.data.name}
        actions={
          <>
            <BackButton to={routes.AGENTS} label="Agents" />
            {isOrchestrationAgent(agent.data) && (
              <span
                id="agent-thread-management-actions"
                style={{ display: 'contents' }}
                aria-label="Thread management"
              />
            )}
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setTestOpen(true)}
              title="Run this agent ad-hoc (nothing is recorded)"
            >
              Test
            </button>
            <button
              type="button"
              className="btn btn--destructive btn--sm"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={remove.isPending}
            >
              Delete
            </button>
          </>
        }
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (!remove.isPending) setDeleteConfirmOpen(false);
        }}
        onConfirm={() => remove.mutate()}
        eyebrow={agent.data.name}
        title="Delete agent"
        message="This stops any of its live threads and cannot be undone."
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete agent'}
        destructive={true}
        pending={remove.isPending}
      />
      {isOrchestrationAgent(agent.data) && orchestrationGroup.isLoading && <Spinner />}
      {isOrchestrationAgent(agent.data) && orchestrationGroup.error && (
        <div className="alert alert--danger" style={{ marginBottom: 'var(--sp-4)' }}>
          Failed to load persistent threads: {(orchestrationGroup.error as Error).message}
        </div>
      )}
      {orchestrationGroup.data && (
        <>
          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <GroupCard
              def={orchestrationGroup.data}
              title="Threads"
              managementActionsTargetId="agent-thread-management-actions"
            />
          </div>
          <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
            <div className="card-body" style={stack3}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong>Orchestration group</strong>
              </div>
              <select
                className="select-native"
                value={orchestrationGroupChoice}
                onChange={(e) => setOrchestrationGroupChoice(e.target.value)}
              >
                {(orchestrationGroups.data ?? []).map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                    {group.isFavorite ? ' ★' : ''}
                  </option>
                ))}
                <option value="__new__">+ New group…</option>
              </select>
              {orchestrationGroupChoice === '__new__' && (
                <input
                  className="input"
                  value={newOrchestrationGroupName}
                  onChange={(e) => setNewOrchestrationGroupName(e.target.value)}
                  placeholder="New group name"
                />
              )}
              <div>
                <button
                  className="btn btn--secondary btn--sm"
                  disabled={
                    moveOrchestrationGroup.isPending ||
                    !orchestrationGroupChoice ||
                    (orchestrationGroupChoice === '__new__' && !newOrchestrationGroupName.trim()) ||
                    orchestrationGroupChoice === orchestrationGroup.data.orchestrationGroupId
                  }
                  onClick={() => moveOrchestrationGroup.mutate()}
                >
                  {moveOrchestrationGroup.isPending ? 'Moving…' : 'Move agent'}
                </button>
              </div>
              {moveOrchestrationGroup.error && (
                <div className="alert alert--danger">
                  {(moveOrchestrationGroup.error as Error).message}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: 'var(--sp-4)',
          alignItems: 'start',
        }}
      >
        <div className="card">
          <div className="card-body">
            <form
              style={stack3}
              onSubmit={(e) => {
                e.preventDefault();
                update.mutate();
              }}
            >
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={true}
                />
              </label>
              <label className="field">
                <span className="field-label">Description (optional)</span>
                <input
                  className="input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Model config</span>
                <select
                  className="select-native"
                  value={modelConfigId}
                  onChange={(e) => setModelConfigId(e.target.value)}
                >
                  <option value="">— None / inherit default —</option>
                  {sortModelsOpenAiFirst(models.data?.items ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.provider} · {m.modelId})
                    </option>
                  ))}
                </select>
                <span className="field-help">
                  Which LLM this agent uses. Leave blank to inherit the org default.
                </span>
              </label>
              <label className="field">
                <span className="field-label">Instructions</span>
                <HandlebarsInstructionsEditor
                  value={instructions}
                  onChange={setInstructions}
                  required={true}
                  rows={10}
                />
                <span className="field-help">
                  System prompt the agent follows. Plain text or Markdown. Use{' '}
                  <code>{`{{token}}`}</code> placeholders to interpolate node input at runtime
                  (requires the workflow node to enable &quot;Interpolate instructions&quot;).
                </span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={hitlAllowed}
                  onChange={(e) => setHitlAllowed(e.target.checked)}
                />
                <span className="t-sm">Allow HITL pause requests from this agent</span>
              </label>
              {isOrchestrationAgent(agent.data) && hitlAllowed && (
                <label className="field">
                  <span className="field-label">When it asks a human a question</span>
                  <select
                    className="select-native"
                    value={elicitationMode}
                    onChange={(e) =>
                      setElicitationMode(e.target.value as 'BLOCKING' | 'NON_BLOCKING')
                    }
                  >
                    <option value="BLOCKING">Blocking — pause until answered</option>
                    <option value="NON_BLOCKING">Non-blocking — keep working, get notified</option>
                  </select>
                  <span className="field-help">
                    {elicitationMode === 'BLOCKING'
                      ? 'The thread pauses until a human answers (or the ask expires).'
                      : 'The thread keeps working; the answer arrives later as a notification with both the question and the answer.'}
                  </span>
                </label>
              )}
              {isOrchestrationAgent(agent.data) && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-2)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={dashboardsAllowed}
                    disabled={toggleDashboardsAllowed.isPending}
                    onChange={(e) => {
                      setDashboardsAllowed(e.target.checked);
                      toggleDashboardsAllowed.mutate(e.target.checked);
                    }}
                  />
                  <span className="t-sm">Can create dashboards/widgets</span>
                </label>
              )}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={imageDescriptionAllowed}
                  onChange={(e) => setImageDescriptionAllowed(e.target.checked)}
                />
                <span className="t-sm">
                  Allow LLM image description
                  <span className="t-xs t-muted" style={{ display: 'block' }}>
                    Exposes a <code>describe_image(url, prompt)</code> tool that runs the
                    agent&apos;s own model on an image URL — lets the agent &quot;see&quot; images
                    the chat prompt can&apos;t pass inline.
                  </span>
                </span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={videoDescriptionAllowed}
                  onChange={(e) => setVideoDescriptionAllowed(e.target.checked)}
                />
                <span className="t-sm">
                  Allow LLM video description
                  <span className="t-xs t-muted" style={{ display: 'block' }}>
                    Exposes <code>describe_video(url, prompt)</code> (first frame through the
                    agent&apos;s own model), <code>create_video_storyboard(url, rows, cols)</code>{' '}
                    (one labelled contact-sheet image of frames sampled across the whole clip,
                    uploaded to object storage — its URL can be handed to describe_image or another
                    agent for scene segmentation) and{' '}
                    <code>extract_video_frame(url, timeSeconds)</code> (a single full-resolution
                    frame at a point in time, uploaded the same way) and{' '}
                    <code>get_video_metadata(url)</code> (ffprobe facts: resolution, duration,
                    bitrate, codec, frame rate, creation time, camera tags — no model call).
                  </span>
                </span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-2)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={codeMode}
                  onChange={(e) => setCodeMode(e.target.checked)}
                />
                <span className="t-sm">
                  Enable code mode
                  <span className="t-xs t-muted" style={{ display: 'block' }}>
                    Gives persistent agents an <code>execute_typescript</code> tool that runs
                    model-authored TypeScript in a sandbox, so one turn can drive multiple tools
                    concurrently (e.g. a drone fleet via <code>Promise.all</code>). Applies the next
                    time the host builds the agent — to update one already running, stop and start
                    it (or restart the host).
                  </span>
                </span>
              </label>
              {isOrchestrationAgent(agent.data) && (
                <div className="field">
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                      cursor: 'pointer',
                      marginTop: 'var(--sp-2)',
                    }}
                  >
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={memoryDisabled}
                      onChange={(e) => setMemoryDisabled(e.target.checked)}
                    />
                    <span className="t-sm">
                      Disable memory (ablate)
                      <span className="t-xs t-muted" style={{ display: 'block' }}>
                        Skips memory injection and the memory tools — the clean &quot;memory
                        OFF&quot; control for benchmarks and debugging. Observational/Working Memory
                        (automatic context compression) stays on and is not configurable.
                      </span>
                    </span>
                  </label>
                </div>
              )}
              {isOrchestrationAgent(agent.data) && (
                <label className="field">
                  <span className="field-label">Continuity (host duty)</span>
                  <select
                    className="select-native"
                    value={continuityMode}
                    onChange={(e) =>
                      setContinuityMode(e.target.value as 'off' | 'mission' | 'always')
                    }
                  >
                    <option value="off">Off — purely reactive (default)</option>
                    <option value="mission">
                      Mission — tick only while a mission/goal is open
                    </option>
                    <option value="always">
                      Always — tick within a ~5 min window after any event
                    </option>
                  </select>
                  <span className="field-help">
                    Whether the host re-arms control ticks so an idle thread keeps tending its
                    mission between events. Applies on the next host build.
                  </span>
                </label>
              )}
              {isOrchestrationAgent(agent.data) && (
                <div className="field">
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={reflectionEnabled}
                      onChange={(e) => setReflectionEnabled(e.target.checked)}
                    />
                    <span className="t-sm">
                      Enable reflection &amp; memory charter
                      <span className="t-xs t-muted" style={{ display: 'block' }}>
                        Seeds a scheduled reflection heartbeat so this supervisor turns recurring
                        worker exceptions into governed memories, and appends its memory charter to
                        its instructions. Applies the next time the host builds the agent — reset a
                        running thread to apply.
                      </span>
                    </span>
                  </label>
                  {reflectionEnabled && (
                    <div
                      style={{
                        display: 'grid',
                        gap: 'var(--sp-3)',
                        marginTop: 'var(--sp-2)',
                        paddingLeft: 'var(--sp-5)',
                      }}
                    >
                      <label className="field">
                        <span className="field-label">Cadence (cron)</span>
                        <input
                          className="input"
                          value={reflectionCron}
                          placeholder="0 */6 * * *  (default: every 6 hours)"
                          onChange={(e) => setReflectionCron(e.target.value)}
                        />
                        <span className="field-help">
                          5-part cron driving the reflection heartbeat. Leave blank for the default
                          cadence.
                        </span>
                      </label>
                      <label className="field">
                        <span className="field-label">Memory charter</span>
                        <textarea
                          className="textarea"
                          rows={6}
                          value={reflectionCharter}
                          placeholder="Leave blank to use the built-in charter. Describe what THIS fleet should remember — recurring failures, fixes that worked, environmental quirks — and what to ignore (one-off noise)."
                          onChange={(e) => setReflectionCharter(e.target.value)}
                        />
                        <span className="field-help">
                          The &quot;what to remember&quot; guide appended to the agent&apos;s
                          instructions when reflection runs. Blank falls back to the host default.
                        </span>
                      </label>
                      <label className="field">
                        <span className="field-label">Reflection prompt (advanced)</span>
                        <textarea
                          className="textarea"
                          rows={4}
                          value={reflectionPrompt}
                          placeholder="Leave blank to use the default reflection-pass prompt."
                          onChange={(e) => setReflectionPrompt(e.target.value)}
                        />
                        <span className="field-help">
                          Overrides the wake prompt delivered on each reflection beat. Rarely needed
                          — blank uses the default.
                        </span>
                      </label>
                      <div className="field">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={runReflectionNow.isPending}
                            onClick={() => runReflectionNow.mutate()}
                          >
                            {runReflectionNow.isPending ? 'Nudging…' : 'Run reflection now'}
                          </button>
                          {reflectionRunFeedback ? (
                            <span className="t-xs t-muted">{reflectionRunFeedback}</span>
                          ) : null}
                        </div>
                        <span className="field-help">
                          Fires each live thread&apos;s reflection heartbeat immediately (delivered
                          when the thread is idle) instead of waiting for the next scheduled beat.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/*
                Output mode selector — `text` (null outputSchema) lets the LLM
                respond in free-form prose; `structured` exposes the JSON
                Schema builder and asks the LLM to produce matching JSON. The
                mode is derived from `outputSchema === null`; toggling between
                modes flips between `null` and `{}` (the empty starting point
                for a new schema, which the builder turns into a real shape).
              */}
              <label className="field">
                <span className="field-label">Output mode</span>
                <select
                  className="select-native"
                  value={outputSchema === null ? 'text' : 'structured'}
                  onChange={(e) => setOutputSchema(e.target.value === 'structured' ? {} : null)}
                >
                  <option value="text">Text — free-form response</option>
                  <option value="structured">Structured — JSON matching a schema</option>
                </select>
                <span className="field-help">
                  In <code>structured</code> mode the LLM is asked to produce JSON matching the
                  schema below; parsed fields are exposed alongside <code>text</code> on the
                  <code> llm-agent</code> node output. <code>text</code> mode keeps the response
                  free-form.
                </span>
              </label>
              {outputSchema !== null ? (
                <div className="field">
                  <span className="field-label">Output schema</span>
                  <JsonSchemaBuilder
                    value={outputSchema}
                    onChange={(next) => setOutputSchema(next ?? {})}
                  />
                  <span className="field-help">
                    Defines the shape the LLM must produce. Clearing all fields keeps you in
                    structured mode with an empty contract — switch the mode above to{' '}
                    <code>text</code> to drop structured output entirely.
                  </span>
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={isAgentSaveDisabled(update.isPending, isDirty)}
                  title={!isDirty ? 'No unsaved changes' : undefined}
                >
                  {update.isPending ? 'Saving…' : isDirty ? 'Save' : 'Saved'}
                </button>
                {update.error ? (
                  <span
                    className="t-xs"
                    style={{ color: 'var(--status-danger)', alignSelf: 'center' }}
                  >
                    {(update.error as Error).message}
                  </span>
                ) : null}
              </div>
            </form>
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <span className="t-mono-label">AGENT · TOOLS</span>
            <h3 className="t-h3">Tools</h3>
          </div>
          <div className="card-body">
            <ToolsSection agent={agent.data} client={client} />
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <span className="t-mono-label">AGENT · MEMORY &amp; SKILLS</span>
            <h3 className="t-h3">Memory &amp; skills this agent sees</h3>
          </div>
          <div className="card-body">
            <AgentMemoryAndSkillsSection agentId={agentId} />
          </div>
        </div>
      </div>
      <p className="t-xs t-muted" style={{ marginTop: 'var(--sp-3)' }}>
        Created {formatDateTime(agent.data.createdAt)} · Updated{' '}
        {formatDateTime(agent.data.updatedAt)} ·{' '}
        <Link to={routes.AGENTS} style={{ color: 'var(--horizon-glow)' }}>
          Back to agents
        </Link>
      </p>
      <AgentTestModal
        open={testOpen}
        onClose={() => setTestOpen(false)}
        agent={agent.data}
        instructions={instructions}
      />
    </>
  );
}

function ToolsSection({ agent, client }: { agent: Agent; client: CortexClient }) {
  const qc = useQueryClient();
  // Single PATCH for both toolRefs and toolAllowlists — some edits (removing a
  // connection that had an allowlist) change both at once, and re-sending the
  // unchanged field is harmless.
  const save = useMutation({
    mutationFn: (body: { toolRefs: string[]; toolAllowlists: Record<string, string[]> }) =>
      client.http.patch(`v1/agents/${agent.id}`, { json: body }).json<Agent>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent', agent.id] }),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <McpToolsEditor
        toolRefs={agent.toolRefs}
        toolAllowlists={(agent.toolAllowlists ?? {}) as Record<string, string[]>}
        onChange={(next) => save.mutate(next)}
        disabled={save.isPending}
      />
      {save.error ? (
        <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
          {(save.error as Error).message}
        </span>
      ) : null}
      {save.isPending ? <span className="t-xs t-muted">Saving…</span> : null}
    </div>
  );
}
